import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { QuizStepper } from "@/components/QuizStepper";
import { QuestionCard } from "@/components/QuestionCard";
import { InterstitialCard } from "@/components/InterstitialCard";
import { AIAnalysisLoader } from "@/components/AIAnalysisLoader";
import { QuizFinalLoader } from "@/components/QuizFinalLoader";
import { B2CExitScreen } from "@/components/B2CExitScreen";

import { questions, calculateScore, getScoreBand, Question } from "@/data/questions";
import { interstitials } from "@/data/interstitials";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { useLanguageWithOverride } from "@/hooks/use-browser-language";
import { getQuizTranslation } from "@/i18n/quiz";

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguageWithOverride();
  const t = getQuizTranslation(language);
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [dynamicQuestions, setDynamicQuestions] = useState<Record<number, Question>>({});
  const [loadingDynamic, setLoadingDynamic] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState<Set<number>>(new Set());
  const [backgroundAnalysisRunning, setBackgroundAnalysisRunning] = useState(false);
  const [aiAnalysisState, setAiAnalysisState] = useState<{
    isRunning: boolean;
    stage: 'analyzing' | 'searching' | 'verifying' | 'complete';
    progress: number;
  }>({
    isRunning: false,
    stage: 'analyzing',
    progress: 0
  });
  const [competitorDomain, setCompetitorDomain] = useState<string | null>(null);
  const [hasCompetitors, setHasCompetitors] = useState<'unknown' | 'yes' | 'no'>('unknown');
  const [showB2CExit, setShowB2CExit] = useState(false);
  const [websiteScan, setWebsiteScan] = useState<any>(null);
  const [foundLinkedInProfile, setFoundLinkedInProfile] = useState<{
    linkedinUrl: string;
    title: string;
    score: number;
    logo?: string;
    followerCount?: number | null;
    employeeCount?: number | null;
    description?: string | null;
    industry?: string | null;
    avgEngagement?: number | null;
    postingFrequency?: string | null;
    recentPostsCount?: number;
  } | null>(null);
  const [competitorWaitStartedAt, setCompetitorWaitStartedAt] = useState<number | null>(null);
  const [linkedinWaitStartedAt, setLinkedinWaitStartedAt] = useState<number | null>(null);
  // Explicit lifecycle of the LinkedIn company lookup
  const [linkedinSearchStatus, setLinkedinSearchStatus] = useState<'idle' | 'running' | 'found' | 'not_found'>('idle');
  const linkedinSearchRunning = linkedinSearchStatus === 'running';
  const setLinkedinSearchRunning = (running: boolean) =>
    setLinkedinSearchStatus(prev => (running ? 'running' : prev === 'running' ? 'not_found' : prev));
  // Guards so the quiz can only ever be saved once per session
  const savingRef = useRef(false);
  const savedRef = useRef(false);
  const quizIdRef = useRef<string | null>(null);
  
  // Reset quiz state when starting a new session
  useEffect(() => {
    if (!location.state?.continue) {
      setAnswers({});
      setCurrentQuestion(0);
      setShowInterstitial(false);
      setDynamicQuestions({});
      setSkippedQuestions(new Set());
      
      // Track quiz start
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'QuizStarted', {
          quiz_type: 'LinkedIn Readiness',
          timestamp: new Date().toISOString()
        });
      }
    }
  }, []);
  
  const question = questions[currentQuestion];
  const displayQuestion = question ? (dynamicQuestions[question?.id] || question) : null;
  
  // Check if domain was provided in Q4 to determine if Q21 should be shown
  const domainAnswer = answers[4] as string | undefined;
  const hasDomain = !!(domainAnswer && typeof domainAnswer === 'string' && domainAnswer.trim().length > 0);
  
  const isLastQuestion = currentQuestion === questions.length - 1 || 
    (question?.id === 15 && hasCompetitors === 'no' && !hasDomain) ||
    (question?.id === 16 && !hasDomain);
  const interstitialData = question?.id ? interstitials[question.id] : undefined;
  const hasInterstitial = !!interstitialData;
  const isShowingInterstitial = hasInterstitial && showInterstitial;
  
  // Check if current question should be shown based on conditional logic
  const shouldShowQuestion = !question?.showOnlyIf || 
    (question.dependsOn !== undefined && 
     answers[question.dependsOn] !== undefined && 
     answers[question.dependsOn] === question.showOnlyIf);
  
  const canProceed = !isShowingInterstitial && displayQuestion && (
    displayQuestion.type === 'text' 
      ? displayQuestion.isOptional || (answers[displayQuestion.id] && (answers[displayQuestion.id] as string).trim() !== "")
      : answers[displayQuestion.id] !== undefined && (
        displayQuestion.type === 'single' 
          ? answers[displayQuestion.id] !== "" 
          : (answers[displayQuestion.id] as string[])?.length > 0
      )
  );
  
  // Auto-skip questions that don't meet conditional requirements
  useEffect(() => {
    const currentQ = questions[currentQuestion];
    
    // IMPORTANT: Don't auto-skip Q16 and Q17 - they have dedicated handlers
    // This prevents race condition with handleNext's isLastQuestion logic
    if (currentQ?.id === 16 || currentQ?.id === 17) {
      return;
    }
    
    if (currentQ?.showOnlyIf && currentQ?.dependsOn !== undefined) {
      const dependentAnswer = answers[currentQ.dependsOn];
      
      // Only skip if dependent question HAS been answered AND condition is NOT met
      if (dependentAnswer !== undefined && dependentAnswer !== currentQ.showOnlyIf) {
        logger.debug(`Auto-skipping Q${currentQ.id} - condition not met`);
        setCurrentQuestion(prev => prev + 1);
      }
    }
  }, [currentQuestion, answers]);
  
  // AI analysis will now start only when clicking "Dalej" on Q4
  // Removed auto-start useEffect to prevent premature loader activation
  
  // Helper function to normalize domain consistently
  const normalizeDomain = (domain: string): string => {
    return domain.trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split(/[/?#]/)[0];
  };
  
  // Start timeout timer when user arrives at Q16 and is waiting for competitor analysis
  useEffect(() => {
    const currentQ = questions[currentQuestion];
    
    if (
      currentQ?.id === 16 &&
      hasCompetitors === 'unknown' &&
      backgroundAnalysisRunning &&
      !dynamicQuestions[16] &&
      !skippedQuestions.has(16)
    ) {
      if (!competitorWaitStartedAt) {
        setCompetitorWaitStartedAt(Date.now());
        logger.info('Q16: Started 35s timeout timer for competitor analysis');
      }
    } else {
      if (competitorWaitStartedAt) {
        logger.info('Q16: Reset timeout timer (analysis finished or question skipped)');
      }
      setCompetitorWaitStartedAt(null);
    }
  }, [currentQuestion, hasCompetitors, backgroundAnalysisRunning, dynamicQuestions, skippedQuestions, competitorWaitStartedAt]);

  // Timeout handler - force skip Q16 after 35s if still waiting
  useEffect(() => {
    if (!competitorWaitStartedAt) return;

    const TIMEOUT_MS = 35000; // 35 seconds
    const timer = setInterval(() => {
      const elapsed = Date.now() - competitorWaitStartedAt;
      if (elapsed >= TIMEOUT_MS) {
        logger.error('Q16: Competitor analysis timeout (35s exceeded), force skipping question');

        setHasCompetitors('no');
        setSkippedQuestions(prev => new Set(prev).add(16));
        setDynamicQuestions(prev => {
          const { 16: _omit, ...rest } = prev as any;
          return rest;
        });
        setBackgroundAnalysisRunning(false);

        toast({
          title: "Analiza konkurencji trwa zbyt długo",
          description: "Pomijamy to pytanie i przechodzimy dalej do wyniku.",
        });

        // Let Q16 useEffect handle navigation (will try Q17 or save)

        setCompetitorWaitStartedAt(null);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [competitorWaitStartedAt]);

  // Timeout mechanism for Q17 LinkedIn search
  useEffect(() => {
    const currentQ = questions[currentQuestion];
    
    if (
      currentQ?.id === 17 &&
      linkedinSearchRunning &&
      !dynamicQuestions[17] &&
      !skippedQuestions.has(17) &&
      foundLinkedInProfile === null
    ) {
      if (!linkedinWaitStartedAt) {
        setLinkedinWaitStartedAt(Date.now());
        logger.info('Q17: Started 35s timeout timer for LinkedIn search');
      }
    } else {
      if (linkedinWaitStartedAt) {
        logger.info('Q17: Reset timeout timer (search finished or question skipped)');
      }
      setLinkedinWaitStartedAt(null);
    }
  }, [currentQuestion, linkedinSearchRunning, dynamicQuestions, skippedQuestions, foundLinkedInProfile, linkedinWaitStartedAt]);

  useEffect(() => {
    if (!linkedinWaitStartedAt) return;

    const TIMEOUT_MS = 35000; // 35 seconds
    const timer = setInterval(() => {
      const elapsed = Date.now() - linkedinWaitStartedAt;
      if (elapsed >= TIMEOUT_MS) {
        logger.error('Q17: LinkedIn search timeout (35s exceeded), skipping question');

        setSkippedQuestions(prev => new Set(prev).add(17));
        setDynamicQuestions(prev => {
          const { 17: _omit, ...rest } = prev as any;
          return rest;
        });
        setLinkedinSearchRunning(false);

        toast({
          title: "Nie udało się znaleźć profilu LinkedIn",
          description: "Pomijamy to pytanie i przechodzimy dalej.",
        });

        (async () => {
          await saveQuizAndNavigate();
        })();

        setLinkedinWaitStartedAt(null);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [linkedinWaitStartedAt]);

  // Helper: skip past Q16 - either advance to Q17 (if it has a chance) or save & navigate
  const skipQ16AndContinue = () => {
    setSkippedQuestions(prev => new Set(prev).add(16));
    // Q17 has a chance if: profile already found, search running, or dynamic question already loaded
    const q17HasChance =
      !!foundLinkedInProfile ||
      linkedinSearchStatus === 'running' ||
      linkedinSearchStatus === 'idle' ||
      !!dynamicQuestions[17];
    if (q17HasChance && !skippedQuestions.has(17)) {
      logger.info('Q16 skipped - advancing to Q17 (LinkedIn profile question)');
      setCurrentQuestion(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      logger.info('Q16 skipped - Q17 also unavailable, saving quiz');
      setSkippedQuestions(prev => new Set(prev).add(17));
      (async () => {
        await saveQuizAndNavigate();
      })();
    }
  };

  // Handle Q16 (competitors question) - ensure it never shows without valid competitors
  useEffect(() => {
    const currentQ = questions[currentQuestion];
    
    if (currentQ?.id === 16) {
      const domainAnswer = answers[4] as string;
      const normalizedDomain = domainAnswer ? normalizeDomain(domainAnswer) : null;
      
      // No domain - skip Q16, try Q17 or save
      if (!normalizedDomain) {
        logger.debug('Q16: No domain provided');
        skipQ16AndContinue();
        return;
      }
      
      // Domain changed - reset competitor state
      if (competitorDomain !== normalizedDomain) {
        logger.debug(`Q16: Domain changed from ${competitorDomain} to ${normalizedDomain}`);
        setCompetitorDomain(normalizedDomain);
        setDynamicQuestions(prev => {
          const { 16: _omit, ...rest } = prev as any;
          return rest;
        });
        
        // Check cache for new domain
        const cachedData = getCachedCompetitors(normalizedDomain);
        if (cachedData && cachedData.competitors && cachedData.competitors.length >= 2) {
          logger.debug('Q16: Found valid cache for new domain');
          setHasCompetitors('yes');
          loadOptionsFromCache(currentQ, cachedData);
        } else if (cachedData && cachedData.competitors && cachedData.competitors.length < 2) {
          logger.debug('Q16: Cache has insufficient competitors');
          setHasCompetitors('no');
          skipQ16AndContinue();
          return;
        } else {
          logger.debug('Q16: No cache, waiting for background analysis');
          setHasCompetitors('unknown');
        }
      }
      
      // No competitors confirmed - skip
      if (hasCompetitors === 'no') {
        logger.debug('Q16: No competitors available');
        if (!skippedQuestions.has(16)) {
          toast({
            title: "Pomijamy pytanie o konkurencję",
            description: "Nie znaleźliśmy wystarczającej liczby konkurentów dla Twojej domeny.",
          });
        }
        skipQ16AndContinue();
        return;
      }
      
      // Unknown state - check if we should skip
      if (hasCompetitors === 'unknown') {
        if (!backgroundAnalysisRunning) {
          logger.debug('Q16: Analysis complete but no competitors');
          setHasCompetitors('no');
          skipQ16AndContinue();
          return;
        }
        // Otherwise show loader (handled in render)
      }
      
      // Has competitors - try to load options
      if (hasCompetitors === 'yes' && !dynamicQuestions[16]) {
        const cachedData = getCachedCompetitors(normalizedDomain);
        if (cachedData && cachedData.competitors && cachedData.competitors.length >= 2) {
          loadOptionsFromCache(currentQ, cachedData);
        } else {
          logger.debug('Q16: Expected cache but not found');
          setHasCompetitors('no');
          skipQ16AndContinue();
        }
      }
    }
  }, [currentQuestion, answers, dynamicQuestions, skippedQuestions, backgroundAnalysisRunning, competitorDomain, hasCompetitors, foundLinkedInProfile, linkedinSearchRunning]);

  // Handle Q17 (LinkedIn profile confirmation) - skip cleanly if nothing was found
  useEffect(() => {
    const currentQ = questions[currentQuestion];
    if (currentQ?.id !== 17) return;
    if (skippedQuestions.has(17)) return;
    // If we have a dynamic question loaded or profile found, let user answer
    if (dynamicQuestions[17] || foundLinkedInProfile) return;
    // If search is still running, let the existing timeout handler manage it
    if (linkedinSearchStatus === 'running') return;
    // Search never started (e.g. interstitial flow) - start it now instead of skipping
    if (linkedinSearchStatus === 'idle') {
      const domainAnswer = answers[4] as string | undefined;
      const normalizedDomain = domainAnswer ? normalizeDomain(domainAnswer) : null;
      if (normalizedDomain) {
        logger.info('Q17: LinkedIn search had not started - starting it now');
        searchLinkedInProfile(normalizedDomain).catch(err => {
          logger.error('LinkedIn search error (Q17 fallback)', err);
        });
        return;
      }
    }
    // Search finished, nothing found - mark and save
    logger.info('Q17: No LinkedIn profile found, marking as no_profile_detected');
    setSkippedQuestions(prev => new Set(prev).add(17));
    setAnswers(prev => ({ ...prev, 17: 'no_profile_detected' }));
    (async () => {
      await saveQuizAndNavigate({ ...answers, 17: 'no_profile_detected' });
    })();
  }, [currentQuestion, dynamicQuestions, foundLinkedInProfile, linkedinSearchStatus, skippedQuestions]);

  
  // Central function to save quiz submission and navigate to results
  async function saveQuizAndNavigate(answersOverride?: Record<number, string | string[]>) {
    // Guard: only ever save/notify once per quiz session
    if (savingRef.current || savedRef.current) {
      logger.info('[SAVE] Skipped - quiz already saving or saved');
      return;
    }
    savingRef.current = true;
    logger.info('[SAVE] Starting saveQuizAndNavigate');
    const finalAnswers = answersOverride || answers;
    const score = calculateScore(finalAnswers);
    const band = getScoreBand(score);
    
    logger.info(`[SAVE] Calculated score: ${score}, band: ${band}`);
    
    // Extract interstitial answers (declare here so it's available in catch block)
    const interstitialAnswers: Record<string, string> = {};
    Object.keys(finalAnswers).forEach(key => {
      if (key.startsWith('interstitial_')) {
        interstitialAnswers[key] = finalAnswers[key as any] as string;
      }
    });
    
    try {
      const domainInput = finalAnswers[4] ? normalizeDomain(String(finalAnswers[4])) : null;
      const selectedCompetitors = Array.isArray(finalAnswers[16]) ? finalAnswers[16] : null;
      
      logger.info(`[SAVE] Domain: ${domainInput}, Competitors: ${selectedCompetitors?.length || 0}`);
      
      // Get competitor analysis from cache
      let competitorAnalysis = null;
      if (domainInput) {
        const cachedData = getCachedCompetitors(domainInput);
        if (cachedData) {
          competitorAnalysis = cachedData;
          logger.info(`[SAVE] Found cached competitor analysis`);
        }
      }
      
      logger.info('[SAVE] Attempting INSERT to quiz_submissions...');
      
      // Generate ID client-side since RLS prevents reading after insert
      // Reuse the same ID for the whole session so retries never create a second record
      if (!quizIdRef.current) {
        quizIdRef.current = crypto.randomUUID();
      }
      const quizSubmissionId = quizIdRef.current;
      
      console.log('📝 Quiz Submission Data:', {
        id: quizSubmissionId,
        score,
        band,
        domainInput,
        competitorAnalysisExists: !!competitorAnalysis,
        selectedCompetitorsCount: selectedCompetitors?.length || 0,
        answersCount: Object.keys(finalAnswers).length,
        hasLinkedInProfile: !!foundLinkedInProfile
      });
      
      // Build submission data with interstitial answers embedded
      const quizAnswersWithMeta: any = { ...finalAnswers };
      if (foundLinkedInProfile) {
        quizAnswersWithMeta.found_linkedin_profile = foundLinkedInProfile;
      }
      // Record whether the LinkedIn lookup actually ran (vs. never checked)
      quizAnswersWithMeta.linkedin_search_status = linkedinSearchStatus;
      // Embed interstitial answers in quiz_answers
      Object.keys(interstitialAnswers).forEach(key => {
        quizAnswersWithMeta[key] = interstitialAnswers[key];
      });
      
      const submissionData: any = {
        id: quizSubmissionId,
        quiz_score: score,
        quiz_band: band,
        quiz_answers: quizAnswersWithMeta,
        domain_input: domainInput,
        competitor_analysis: competitorAnalysis,
        selected_competitors: selectedCompetitors,
        website_scan: websiteScan,
        session_id: localStorage.getItem('headline_session_id')
      };
      
      const { error } = await supabase
        .from('quiz_submissions')
        .insert(submissionData);
      
      if (error) {
        logger.error('[SAVE] ❌ Error saving quiz submission', error);
        console.error('❌ Quiz Submission Error Details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        
        // Track quiz submission failure
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('trackCustom', 'QuizSubmissionFailed', {
            error_message: error.message || 'Unknown error',
            score: score,
            band: band
          });
        }
        
        navigate('/wynik', { 
          state: { 
            score, 
            band, 
            answers: finalAnswers, 
            quizSubmissionId, 
            websiteScan,
            foundLinkedInProfile,
            interstitialAnswers
          } 
        });
      } else {
        savedRef.current = true;
        logger.info(`[SAVE] ✅ Quiz submission saved with ID: ${quizSubmissionId}`);
        console.log('✅ Quiz saved successfully to database:', quizSubmissionId);
        
        // Send Telegram notification for quiz-only submissions (without lead)
        try {
          await supabase.functions.invoke('notify-quiz-submission', {
            body: { quizId: quizSubmissionId }
          });
        } catch (notifyError) {
          console.error('Failed to send quiz notification:', notifyError);
          // Don't fail the quiz submission if notification fails
        }
        
        // Track quiz completion
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('trackCustom', 'QuizCompleted', {
            score: score,
            band: band,
            total_questions_answered: Object.keys(finalAnswers).length,
            domain_provided: !!domainInput,
            competitors_selected: selectedCompetitors?.length || 0
          });
          
          // Also track as standard Complete Registration event
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'LinkedIn Readiness Quiz',
            status: 'completed',
            value: score
          });
        }
        
        navigate('/wynik', { 
          state: { 
            score, 
            band, 
            answers: finalAnswers, 
            quizSubmissionId, 
            websiteScan,
            foundLinkedInProfile,
            interstitialAnswers
          } 
        });
      }
    } catch (err) {
      logger.error('[SAVE] ❌ Exception in saveQuizAndNavigate', err);
      
      // Track quiz submission exception
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'QuizSubmissionFailed', {
          error_message: err instanceof Error ? err.message : 'Unknown exception',
          error_type: 'exception',
          score: score,
          band: band
        });
      }
      
      navigate('/wynik', { 
        state: { 
          score, 
          band, 
          answers: finalAnswers, 
          quizSubmissionId: '', 
          websiteScan,
          foundLinkedInProfile,
          interstitialAnswers
        } 
      });
    }
  }
  
  function getCachedCompetitors(domain: string) {
    try {
      const normalizedDomain = normalizeDomain(domain);
      const cached = localStorage.getItem(`competitors_${normalizedDomain}`);
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      const TTL = 24 * 60 * 60 * 1000; // 24 hours
      
      if (Date.now() - data.timestamp > TTL) {
        localStorage.removeItem(`competitors_${normalizedDomain}`);
        return null;
      }
      
      // Validate cached data quality
      const result = data.data;
      if (
        !result.competitors ||
        !Array.isArray(result.competitors) ||
        result.competitors.length < 2
      ) {
        logger.debug('Cached data has insufficient competitors, removing');
        localStorage.removeItem(`competitors_${normalizedDomain}`);
        return null;
      }
      
      return result;
    } catch {
      return null;
    }
  }
  
  function cacheCompetitors(domain: string, data: any) {
    try {
      const normalizedDomain = normalizeDomain(domain);
      const cacheData = {
        domain: normalizedDomain,
        timestamp: Date.now(),
        ttl: 24 * 60 * 60 * 1000,
        data
      };
      localStorage.setItem(`competitors_${normalizedDomain}`, JSON.stringify(cacheData));
    } catch (err) {
      console.error('Failed to cache competitors:', err);
    }
  }
  
  function loadOptionsFromCache(question: Question, cachedData: any) {
    logger.debug('Loading options from cache for question', question.id);
    
    if (!cachedData.competitors || cachedData.competitors.length < 2) {
      logger.debug('Not enough competitors, skipping question');
      setSkippedQuestions(prev => new Set(prev).add(question.id));
      return;
    }
    
    const options = cachedData.competitors.map((comp: any) => ({
      label: comp.company_name || comp.domain || 'Unknown Company',
      value: comp.domain,
      score: 0.5,
      url: comp.domain
    }));
    
    logger.debug(`Loaded ${options.length} competitor options:`, options.map(o => o.label).join(', '));
    
    options.push({ 
      label: "Nie znam żadnej z tych firm", 
      value: "none", 
      score: 0 
    });
    
    logger.debug('Setting dynamic options for question');
    
    setDynamicQuestions(prev => ({
      ...prev,
      [question.id]: { ...question, options }
    }));
  }
  
  // Helper to clean LinkedIn URL - remove query parameters
  function cleanLinkedInUrl(url: string): string {
    if (!url) return url;
    const urlWithoutQuery = url.split('?')[0];
    // Ensure it ends with / for consistency
    return urlWithoutQuery.endsWith('/') ? urlWithoutQuery : `${urlWithoutQuery}/`;
  }

  async function searchLinkedInProfile(domain: string) {
    logger.info('Starting LinkedIn profile search with Bright Data for domain');
    setLinkedinSearchStatus('running');
    let outcome: 'found' | 'not_found' = 'not_found';
    
    try {
      // Use new edge function that combines search + Bright Data enrichment
      const { data, error } = await supabase.functions.invoke('find-linkedin-by-domain', {
        body: { domain }
      });

      if (error) {
        logger.error('LinkedIn search error', error);
        return;
      }

      if (data.success && data.profile) {
        const profile = data.profile;
        const cleanedUrl = cleanLinkedInUrl(profile.linkedinUrl);
        
        // Map Bright Data response to our profile format
        const profileData = {
          linkedinUrl: cleanedUrl,
          title: profile.name || domain,
          score: 1.0,
          logo: profile.logo || null,
          followerCount: profile.followers || null,
          employeeCount: profile.employees_in_linkedin || null,
          description: profile.about || null,
          industry: profile.industries || null,
          avgEngagement: null,
          postingFrequency: null,
          recentPostsCount: 0,
          // Store all Bright Data enriched fields
          enriched: profile.enriched || false,
          founded: profile.founded || null,
          headquarters: profile.headquarters || null,
          website: profile.website || null,
          company_size: profile.company_size || null,
          locations: profile.locations || [],
          country_code: profile.country_code || null,
          specialties: profile.specialties || null,
          slogan: profile.slogan || null,
          organization_type: profile.organization_type || null,
          company_id: profile.company_id || null,
          _raw: profile._raw || null
        };
        
        setFoundLinkedInProfile(profileData);
        outcome = 'found';
        logger.info(`LinkedIn profile found with full data: ${profileData.linkedinUrl}`);
        
        // Add dynamic question 17 with profile info
        setDynamicQuestions(prev => ({
          ...prev,
          17: {
            ...questions.find(q => q.id === 17)!,
            question: `Czy to jest profil LinkedIn Waszej firmy?\n\n${profile.name || domain}\n${cleanedUrl}`,
            metadata: {
              logo: profileData.logo,
              companyName: profile.name || domain,
              linkedinUrl: cleanedUrl,
              enrichedData: profileData
            }
          }
        }));
        logger.info('Q17: Dynamic question created with LinkedIn profile data');
      } else {
        logger.debug('LinkedIn profile not found');
      }
    } catch (err) {
      logger.error('LinkedIn search error:', err);
      // Don't block quiz if search fails
    } finally {
      setLinkedinSearchStatus(outcome);
    }
  }
  
  async function startAIAnalysisInBackground(domain: string) {
    logger.info('Starting background AI analysis for domain');
    
    const normalizedDomain = normalizeDomain(domain);
    
    // Clear cache and force fresh analysis
    localStorage.removeItem(`competitors_${normalizedDomain}`);
    logger.info('Cache cleared for fresh analysis');
    
    setBackgroundAnalysisRunning(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('find-competitors', {
        body: { 
          domain: normalizedDomain, 
          forceRefresh: true,
          fastMode: true,
          skipStep3LLM: true
        }
      });
      
      if (error) {
        logger.error('Background AI analysis error', error);
        
        // ALWAYS set state first to prevent race conditions
        setHasCompetitors('no');
        setSkippedQuestions(prev => new Set(prev).add(16));
        setDynamicQuestions(prev => {
          const { 16: _omit, ...rest } = prev as any;
          return rest;
        });
        setBackgroundAnalysisRunning(false);
        
        // Check if it's a domain validation error (SSL, NO_CONTENT, etc.)
        if (error.message && (error.message.includes('INVALID_DOMAIN') || error.message.includes('NO_CONTENT'))) {
          toast({
            title: "Problem z domeną",
            description: "Nie udało się przeanalizować domeny. Quiz będzie kontynuowany bez analizy konkurencji.",
          });
          
          logger.info('[DEBUG] Domain error (SSL/NO_CONTENT) - continuing without competitor analysis');
        } else {
          // Generic error
          toast({
            title: "Analiza konkurencji niedostępna",
            description: "Quiz będzie kontynuowany bez analizy konkurencji.",
          });
        }
        
        // Q16 useEffect will pick this up and advance to Q17 or save

        return;
      }
      
      if (!data.competitors || data.competitors.length < 2) {
        logger.info(`Background AI analysis - insufficient competitors (${data.competitors?.length || 0} found, confidence: ${data.confidence})`);
        setHasCompetitors('no');
        setSkippedQuestions(prev => new Set(prev).add(16));
        setDynamicQuestions(prev => {
          const { 16: _omit, ...rest } = prev as any;
          return rest;
        });
        setBackgroundAnalysisRunning(false);
        
        // Q16 useEffect will handle next step (Q17 or save)

        return;
      }
      
      // Cache the results
      cacheCompetitors(normalizedDomain, data);
      setHasCompetitors('yes');
      
      logger.info(`Background AI analysis complete - ${data.competitors.length} competitors cached`);
      setBackgroundAnalysisRunning(false);
      
      // If we're currently on Q16 and don't have dynamic options yet, load them immediately
      if (questions[currentQuestion]?.id === 16 && !dynamicQuestions[16]) {
        const q16 = questions.find(q => q.id === 16);
        if (q16) {
          logger.info('Q16: Loading competitor options immediately from fresh analysis');
          loadOptionsFromCache(q16, data);
        }
      }
      
    } catch (err) {
      logger.error('Error in background AI analysis', err);
      setHasCompetitors('no');
      setSkippedQuestions(prev => new Set(prev).add(16));
      setDynamicQuestions(prev => {
        const { 16: _omit, ...rest } = prev as any;
        return rest;
      });
      setBackgroundAnalysisRunning(false);
      
      toast({
        title: "Analiza konkurencji niedostępna",
        description: "Quiz będzie kontynuowany bez analizy konkurencji.",
      });
      
      // Q16 useEffect will handle next step (Q17 or save)

    }
  }
  
  
  const handleNext = async () => {
    logger.debug(`[NEXT] Current Q: ${displayQuestion?.id}, isLastQuestion: ${isLastQuestion}, hasCompetitors: ${hasCompetitors}`);
    
    // Track navigation
    if (typeof window !== 'undefined' && window.fbq && displayQuestion) {
      window.fbq('trackCustom', 'QuizStepCompleted', {
        question_id: displayQuestion.id,
        current_step: currentQuestion + 1,
        total_steps: questions.length,
        progress_percentage: Math.round(((currentQuestion + 1) / questions.length) * 100)
      });
    }
    
    // Start AI analysis in background when clicking "Dalej" on Q4 (text domain input)
    // MUST BE BEFORE interstitial check to ensure analysis starts!
    if (displayQuestion.id === 4) {
      const domain = String(answers[4] || '').trim();
      const normalizedDomain = domain ? normalizeDomain(domain) : null;
      
      // Track domain submission
      if (typeof window !== 'undefined' && window.fbq && normalizedDomain) {
        window.fbq('trackCustom', 'DomainSubmitted', {
          domain: normalizedDomain,
          has_domain: !!normalizedDomain
        });
      }
      
      // CRITICAL: Clear old LinkedIn profile and Q17 data to prevent stale data
      logger.info('Q4: Resetting LinkedIn profile state for new domain');
      setFoundLinkedInProfile(null);
      setLinkedinSearchStatus('idle');
      setDynamicQuestions(prev => {
        const { 16: _omit16, 17: _omit17, ...rest } = prev as any;
        return rest;
      });
      
      // Hard reset for Q16 and Q17
      setSkippedQuestions(prev => {
        const next = new Set(prev);
        next.delete(16);
        next.delete(17);
        return next;
      });
      
      if (!normalizedDomain) {
        // No domain provided - skip Q16 and Q17
        logger.info('Q4: No domain provided - skipping Q16 and Q17');
        setCompetitorDomain(null);
        setHasCompetitors('no');
        setSkippedQuestions(prev => new Set(prev).add(16).add(17));
      } else {
        logger.info(`Q4: Domain input: ${normalizedDomain}`);
        setCompetitorDomain(normalizedDomain);
        
        // ALWAYS start LinkedIn profile search (non-blocking) - regardless of cache
        logger.info('Starting LinkedIn profile search (unconditional)');
        searchLinkedInProfile(normalizedDomain).catch(err => {
          logger.error('LinkedIn search error (non-blocking)', err);
        });
        
        // Start website scan in background (async, don't block)
        (async () => {
          try {
            logger.info(`Starting website scan for: ${normalizedDomain}`);
            const { data: scanData, error: scanError } = await supabase.functions.invoke('scan-website', {
              body: { domain: normalizedDomain }
            });
            
            if (!scanError && scanData) {
              logger.info('Website scan completed successfully');
              setWebsiteScan(scanData.data || scanData);
            } else {
              logger.error('Website scan failed or returned no data', scanError);
            }
          } catch (err) {
            logger.error('Website scan error (non-blocking)', err);
          }
        })();
        
        // Check cache for competitors (separate from LinkedIn search)
        const cachedData = getCachedCompetitors(normalizedDomain);
        if (cachedData && cachedData.competitors && cachedData.competitors.length >= 2) {
          logger.info(`Found ${cachedData.competitors.length} competitors in cache`);
          setHasCompetitors('yes');
          // Don't load options yet - will be loaded when reaching Q20
        } else {
          logger.info('No valid cache - starting background AI competitor analysis');
          setHasCompetitors('unknown');
          // Start AI competitor analysis (don't wait)
          startAIAnalysisInBackground(normalizedDomain).catch(err => {
            logger.error('Background competitor analysis error', err);
          });
        }
      }
      
      // NOW show interstitial after starting all analysis
      if (hasInterstitial && !showInterstitial) {
        setShowInterstitial(true);
        return;
      }
    }
    
    // For other questions, show interstitial before proceeding
    if (hasInterstitial && !showInterstitial && displayQuestion.id !== 4) {
      setShowInterstitial(true);
      return;
    }
    
    // If this is the last question, save and navigate
    if (isLastQuestion) {
      logger.info('[NEXT] Last question reached - saving quiz');
      await saveQuizAndNavigate();
      return;
    } else {
      setCurrentQuestion(prev => prev + 1);
      setShowInterstitial(false);
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleInterstitialContinue = (answer: 'no' | 'yes') => {
    // Save interstitial answer under separate key to not overwrite question answers
    const questionId = question?.id;
    if (questionId) {
      setAnswers(prev => ({
        ...prev,
        [`interstitial_${questionId}`]: answer
      }));
      
      logger.info(`[INTERSTITIAL] Question ${questionId}: ${answer}`);
    }
    
    setCurrentQuestion(prev => prev + 1);
    setShowInterstitial(false);
    
    // Scroll to top when moving to question 8 (index 7)
    if (currentQuestion + 1 === 7) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleBack = () => {
    // Jeśli jesteśmy na interstitial, po prostu go ukryj i zostań na bieżącym pytaniu
    if (showInterstitial) {
      setShowInterstitial(false);
      logger.debug('[BACK] Hiding interstitial, staying on current question');
      return;
    }
    
    // W przeciwnym razie normalnie cofnij do poprzedniego pytania
    if (currentQuestion > 0) {
      // Skip back over any skipped questions
      let prevIndex = currentQuestion - 1;
      while (prevIndex >= 0 && skippedQuestions.has(questions[prevIndex].id)) {
        prevIndex--;
      }
      if (prevIndex >= 0) {
        setCurrentQuestion(prevIndex);
        logger.debug(`[BACK] Moving to question index ${prevIndex} (ID: ${questions[prevIndex]?.id})`);
      } else {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  };
  
  const handleAnswer = (value: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [displayQuestion.id]: value
    }));
    
    // Track answer
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'QuizQuestionAnswered', {
        question_id: displayQuestion.id,
        question_text: displayQuestion.question.substring(0, 100),
        answer_type: displayQuestion.type,
        is_multiple: Array.isArray(value)
      });
    }
    
    // Check for B2C/E-commerce exit on Question 2
    if (displayQuestion.id === 2 && (value === 'b2c' || value === 'ecommerce')) {
      // Track B2C exit for A/B testing
      (async () => {
        try {
          const sessionId = localStorage.getItem('sessionId');
          const variantId = localStorage.getItem('variantId');
          if (sessionId && variantId) {
            await supabase.from('headline_conversions').insert({
              variant_id: variantId,
              session_id: sessionId,
              b2c_exit: true
            });
          }
        } catch (error) {
          console.error('Error tracking B2C exit:', error);
        }
      })();
      
      // Track Meta Pixel event for B2C Exit
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'B2CExitShown', {
          industry_type: value,
          question_id: displayQuestion.id,
          timestamp: new Date().toISOString()
        });
      }
      
      setTimeout(() => {
        setShowB2CExit(true);
      }, 300);
      return; // Stop here, don't proceed
    }
    
    // Auto-advance only for single choice questions (NOT text inputs)
    if (displayQuestion.type === 'single') {
      setTimeout(async () => {
        if (hasInterstitial) {
          setShowInterstitial(true);
        } else if (isLastQuestion) {
          await saveQuizAndNavigate({ ...answers, [displayQuestion.id]: value });
        } else {
          setCurrentQuestion(prev => prev + 1);
          setShowInterstitial(false);
          
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 300);
    }
  };
  
  // Calculate question number (excluding skipped questions)
  const questionNumber = questions.slice(0, currentQuestion + 1).filter((q, idx) => 
    idx <= currentQuestion && !skippedQuestions.has(q.id)
  ).length;

  // Calculate interstitial number
  const interstitialKeys = Object.keys(interstitials).map(Number).sort((a, b) => a - b);
  const currentInterstitialIndex = interstitialKeys.findIndex(key => key === question?.id);
  const interstitialNumber = currentInterstitialIndex >= 0 ? currentInterstitialIndex + 1 : 0;
  
  // Calculate dynamic total steps: real questions + interstitials (excluding skipped)
  const visibleQuestionsCount = questions.filter(q => 
    !skippedQuestions.has(q.id)
  ).length;
  const interstitialsShownCount = interstitialKeys.filter(key => 
    !skippedQuestions.has(key)
  ).length;
  const derivedTotalSteps = visibleQuestionsCount + interstitialsShownCount;
  
  return (
    <div className="min-h-screen gradient-subtle">
      <div className="container px-4 py-12">
        {!showB2CExit && (
          <QuizStepper 
            currentStep={currentQuestion + 1} 
            totalSteps={derivedTotalSteps}
            isInterstitial={isShowingInterstitial}
            questionNumber={questionNumber}
            interstitialNumber={interstitialNumber}
          />
        )}
        
        <div className="mb-12">
      {showB2CExit ? (
        <B2CExitScreen onBackHome={() => navigate('/')} language={language} />
      ) : isShowingInterstitial ? (
            <InterstitialCard
              data={interstitialData}
              onContinue={handleInterstitialContinue}
            />
          ) : !displayQuestion ? (
            <div className="w-full max-w-2xl mx-auto text-center py-12">
              <Loader2 className="animate-spin mx-auto mb-4 h-12 w-12 text-primary" />
              <p className="text-lg font-medium">{language === 'en' ? 'Loading...' : 'Ładowanie...'}</p>
            </div>
          ) : (displayQuestion.isDynamic && !dynamicQuestions[displayQuestion.id] && !skippedQuestions.has(displayQuestion.id)) ? (
            <div className="w-full max-w-2xl mx-auto">
              <QuizFinalLoader language={language} />
            </div>
          ) : (
            <QuestionCard
              question={displayQuestion}
              value={answers[displayQuestion.id] || (displayQuestion.type === 'multiple' ? [] : "")}
              onChange={handleAnswer}
              language={language}
            />
          )}
        </div>
        
        {!showB2CExit && (
          <div className="flex justify-between max-w-2xl mx-auto">
            <Button
              variant="outline"
              onClick={handleBack}
              size="lg"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              {t("back")}
            </Button>
            
            {!isShowingInterstitial && (displayQuestion.type === 'multiple' || displayQuestion.type === 'text') && (
              <Button
                onClick={handleNext}
                disabled={!canProceed || (displayQuestion.type === 'text' && (!answers[displayQuestion.id] || (answers[displayQuestion.id] as string).trim().length === 0))}
                size="lg"
              >
                {isLastQuestion ? t("seeResults") : t("next")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
